// @ts-check
// IR-JSON -> Python (DEC-107).
//
// El resultado tiene que ser Python EJECUTABLE por el sandbox que ya existe
// (`python-editor/sandbox-client.js`), no un esbozo. Mismo contrato que el
// generador de pseudocódigo: o traduce entero o explica qué no ha podido.
//
// ## Las tres correspondencias que no son directas
//
// 1. **`RepetirHasta` no existe en Python.** Se emite el equivalente exacto:
//    `while True:` con la comprobación al FINAL y un `break`. Es lo que
//    escribiría un programador, y conserva la semántica de "el cuerpo se
//    ejecuta al menos una vez", que es justo lo que distingue a este bucle.
//
// 2. **`BucleFinPara` es INCLUSIVO en el límite** (`Para i <- 1 Hasta 10`
//    recorre el 10) y `range` de Python es exclusivo. Traducirlo como
//    `range(1, 10)` daría un programa que hace una vuelta menos: un fallo
//    silencioso, del peor tipo, porque el alumno vería un programa
//    aparentemente correcto que cuenta mal. Se emite `range(inicio, fin + 1)`
//    con paso positivo y `range(inicio, fin - 1, paso)` con paso negativo.
//
// 3. **`Entrada` no lleva tipo la mayoría de las veces.** Cuando la IR declara
//    el tipo se envuelve (`int(input())`); cuando no, se deja `input()` a
//    secas. Convertir "por si acaso" rompería la lectura de un texto.
//
// ## Declaraciones
//
// Python no declara variables. Las `Declaracion` de la IR se traducen a una
// asignación de valor inicial neutro (`0`, `0.0`, `""`, `False`) para que el
// programa funcione igual: sin ellas, un programa que declara y luego lee en
// un bucle fallaría con `NameError` en la primera vuelta.

import { crearRegistroDeMotivos, traduccionFallida, traduccionOk, unirLineas } from './resultado.js';

/** Sangría de Python: cuatro espacios (PEP 8). */
const SANGRIA = '    ';

const VALOR_INICIAL = /** @type {Record<string, string>} */ ({
  entero: '0',
  decimal: '0.0',
  cadena: "''",
  booleano: 'False',
});

const CONVERSION = /** @type {Record<string, string>} */ ({
  entero: 'int',
  decimal: 'float',
  cadena: 'str',
});

const OPERADOR_PYTHON = /** @type {Record<string, string>} */ ({
  '+': '+',
  '-': '-',
  '*': '*',
  '/': '/',
  '%': '%',
  '==': '==',
  '!=': '!=',
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
  Y: 'and',
  O: 'or',
});

/**
 * @param {unknown} valor
 * @returns {valor is Record<string, unknown>}
 */
function esNodo(valor) {
  return typeof valor === 'object' && valor !== null && typeof (/** @type {any} */ (valor).nodo) === 'string';
}

/**
 * Tipos declarados en el programa que se está generando, por nombre de
 * variable. Se rellena en `irAPython` antes de recorrer nada.
 *
 * Es estado de módulo y no un parámetro que atraviese diez funciones porque el
 * generador es de un solo uso y síncrono: no hay dos traducciones a la vez.
 * `irAPython` lo vacía al empezar, así que una traducción nunca hereda los
 * tipos de la anterior.
 * @type {Map<string, string>}
 */
const tiposDeclarados = new Map();

/**
 * @param {unknown} expresionValor
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string}
 */
function expresion(expresionValor, registro, donde) {
  if (!esNodo(expresionValor)) {
    registro.anotar('Hay una expresión vacía o mal formada.', donde);
    return '0';
  }
  switch (expresionValor.nodo) {
    case 'Literal': {
      const valor = expresionValor.valor;
      if (typeof valor === 'string') return JSON.stringify(valor);
      if (typeof valor === 'boolean') return valor ? 'True' : 'False';
      return String(valor);
    }
    case 'ReferenciaVariable':
      return String(expresionValor.nombre ?? '');
    case 'Operacion': {
      const operandos = Array.isArray(expresionValor.operandos) ? expresionValor.operandos : [];
      const bruto = String(expresionValor.operador ?? '');
      if (bruto === 'NEG') return `-(${expresion(operandos[0], registro, donde)})`;
      if (bruto === 'NO') return `not (${expresion(operandos[0], registro, donde)})`;
      const op = OPERADOR_PYTHON[bruto];
      if (!op) {
        registro.anotar(`El operador "${bruto}" no tiene equivalente en Python.`, donde);
        return '0';
      }
      // Se parentiza siempre: sobran paréntesis pero el significado es
      // exactamente el del original, sin depender de acertar la precedencia
      // de dos lenguajes a la vez.
      return `(${expresion(operandos[0], registro, donde)} ${op} ${expresion(operandos[1], registro, donde)})`;
    }
    case 'Llamada': {
      const argumentos = Array.isArray(expresionValor.argumentos) ? expresionValor.argumentos : [];
      const args = argumentos.map((a) => expresion(a, registro, donde)).join(', ');
      return `${String(expresionValor.nombre ?? '')}(${args})`;
    }
    case 'AccesoIndice':
      return `${expresion(expresionValor.lista, registro, donde)}[${expresion(expresionValor.indice, registro, donde)}]`;
    default:
      registro.anotar(`No sé escribir "${String(expresionValor.nodo)}" en Python.`, donde);
      return '0';
  }
}

/**
 * Nombre del tipo primitivo de un nodo de tipo, o `null` si es una lista o
 * algo desconocido.
 * @param {unknown} tipo
 * @returns {string|null}
 */
function tipoPrimitivo(tipo) {
  return typeof tipo === 'string' ? tipo : null;
}

/**
 * @param {unknown} secuencia
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string[]}
 */
function sentencias(secuencia, nivel, registro, donde) {
  const lista =
    esNodo(secuencia) && Array.isArray(secuencia.sentencias) ? secuencia.sentencias : [];
  /** @type {string[]} */
  const salida = [];
  for (const sentencia of lista) {
    salida.push(...unaSentencia(sentencia, nivel, registro, donde));
  }
  // Python no admite un bloque vacío: si la rama no tiene nada, hace falta
  // `pass` o el programa no compila.
  if (salida.length === 0) salida.push(`${SANGRIA.repeat(nivel)}pass`);
  return salida;
}

/**
 * @param {unknown} sentencia
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string[]}
 */
function unaSentencia(sentencia, nivel, registro, donde) {
  const s = SANGRIA.repeat(nivel);
  if (!esNodo(sentencia)) {
    registro.anotar('Hay un bloque vacío o mal formado.', donde);
    return [];
  }
  switch (sentencia.nodo) {
    case 'Declaracion': {
      const primitivo = tipoPrimitivo(sentencia.tipo);
      if (primitivo === null) {
        // Una lista sí tiene inicial natural en Python, a diferencia de
        // pseudocódigo, donde hacía falta un tamaño.
        return [`${s}${String(sentencia.nombre ?? '')} = []`];
      }
      const inicial = VALOR_INICIAL[primitivo];
      if (!inicial) {
        registro.anotar(`El tipo "${primitivo}" no existe en Python.`, donde);
        return [];
      }
      return [`${s}${String(sentencia.nombre ?? '')} = ${inicial}`];
    }
    case 'Asignacion':
      return [
        `${s}${String(sentencia.variable ?? '')} = ${expresion(sentencia.expresion, registro, donde)}`,
      ];
    case 'Entrada': {
      const variable = String(sentencia.variable ?? '');
      // El tipo puede venir en el propio nodo o, si no, de la DECLARACIÓN de
      // esa variable. Usarlo importa mucho más de lo que parece: `input()`
      // devuelve texto, así que un `Leer n` seguido de `n > 5` se traduciría a
      // un programa que revienta al comparar texto con número. La información
      // está en el programa; no aprovecharla sería generar código roto
      // teniendo con qué evitarlo.
      const primitivo = tipoPrimitivo(sentencia.tipo) ?? tiposDeclarados.get(variable) ?? null;
      const conversion = primitivo === null ? null : CONVERSION[primitivo];
      const lectura = conversion ? `${conversion}(input())` : 'input()';
      return [`${s}${variable} = ${lectura}`];
    }
    case 'Salida':
      return [`${s}print(${expresion(sentencia.expresion, registro, donde)})`];
    case 'Comentario':
      return String(sentencia.texto ?? '')
        .split('\n')
        .map((linea) => `${s}# ${linea}`);
    case 'Condicional': {
      const lineas = [
        `${s}if ${expresion(sentencia.condicion, registro, donde)}:`,
        ...sentencias(sentencia.rama_si, nivel + 1, registro, donde),
      ];
      const sino = sentencia.rama_sino;
      if (esNodo(sino) && Array.isArray(sino.sentencias) && sino.sentencias.length > 0) {
        lineas.push(`${s}else:`, ...sentencias(sino, nivel + 1, registro, donde));
      }
      return lineas;
    }
    case 'BucleMientras':
      return [
        `${s}while ${expresion(sentencia.condicion, registro, donde)}:`,
        ...sentencias(sentencia.cuerpo, nivel + 1, registro, donde),
      ];
    case 'BucleFinPara': {
      const variable = String(sentencia.variable ?? '');
      const inicio = expresion(sentencia.inicio, registro, donde);
      const fin = expresion(sentencia.fin, registro, donde);
      const pasoNodo = sentencia.paso;
      const paso = expresion(pasoNodo, registro, donde);
      // El límite de la IR es INCLUSIVO y el de `range` exclusivo: hay que
      // corregirlo o el programa daría una vuelta menos, en silencio.
      const pasoLiteral =
        esNodo(pasoNodo) && pasoNodo.nodo === 'Literal' && typeof pasoNodo.valor === 'number'
          ? pasoNodo.valor
          : null;
      let rango;
      if (pasoLiteral === 1) {
        rango = `range(${inicio}, ${fin} + 1)`;
      } else if (pasoLiteral !== null && pasoLiteral < 0) {
        rango = `range(${inicio}, ${fin} - 1, ${paso})`;
      } else if (pasoLiteral !== null) {
        rango = `range(${inicio}, ${fin} + 1, ${paso})`;
      } else {
        // Paso calculado en tiempo de ejecución: no se puede decidir el signo
        // al traducir. Se emite la corrección que sirve para ambos signos.
        rango = `range(${inicio}, ${fin} + (1 if ${paso} > 0 else -1), ${paso})`;
      }
      return [
        `${s}for ${variable} in ${rango}:`,
        ...sentencias(sentencia.cuerpo, nivel + 1, registro, donde),
      ];
    }
    case 'RepetirHasta': {
      // Equivalente exacto de "ejecuta y comprueba al final".
      const cuerpo = sentencias(sentencia.cuerpo, nivel + 1, registro, donde);
      return [
        `${s}while True:`,
        ...cuerpo,
        `${s}${SANGRIA}if ${expresion(sentencia.condicion, registro, donde)}:`,
        `${s}${SANGRIA}${SANGRIA}break`,
      ];
    }
    case 'Llamada': {
      const argumentos = Array.isArray(sentencia.argumentos) ? sentencia.argumentos : [];
      const args = argumentos.map((a) => expresion(a, registro, donde)).join(', ');
      const llamada = `${String(sentencia.nombre ?? '')}(${args})`;
      const destino = sentencia.variable_destino;
      return [typeof destino === 'string' && destino !== '' ? `${s}${destino} = ${llamada}` : `${s}${llamada}`];
    }
    case 'Retorno':
      if (sentencia.expresion === null || sentencia.expresion === undefined) return [`${s}return`];
      return [`${s}return ${expresion(sentencia.expresion, registro, donde)}`];
    default:
      registro.anotar(`No sé escribir el bloque "${String(sentencia.nodo)}" en Python.`, donde);
      return [];
  }
}

/**
 * Traduce un programa en IR-JSON a Python.
 * @param {unknown} programa
 * @returns {import('./resultado.js').Traduccion<string>}
 */
export function irAPython(programa) {
  const registro = crearRegistroDeMotivos();
  if (!esNodo(programa) || programa.nodo !== 'Programa') {
    return traduccionFallida([{ descripcion: 'No hay ningún programa que traducir.' }]);
  }

  /** @type {string[]} */
  const lineas = [];

  tiposDeclarados.clear();
  for (const declaracion of Array.isArray(programa.declaraciones) ? programa.declaraciones : []) {
    if (!esNodo(declaracion)) continue;
    const primitivo = tipoPrimitivo(declaracion.tipo);
    if (primitivo !== null) tiposDeclarados.set(String(declaracion.nombre ?? ''), primitivo);
  }

  const funciones = Array.isArray(programa.funciones) ? programa.funciones : [];
  for (const funcion of funciones) {
    if (!esNodo(funcion)) continue;
    const nombre = String(funcion.nombre ?? '');
    const parametros = Array.isArray(funcion.parametros) ? funcion.parametros : [];
    const nombresParametros = parametros
      .map((p) => (esNodo(p) ? String(p.nombre ?? '') : ''))
      .filter((n) => n !== '');
    lineas.push(`def ${nombre}(${nombresParametros.join(', ')}):`);
    lineas.push(...sentencias(funcion.cuerpo, 1, registro, `función ${nombre}`));
    lineas.push('');
  }

  const declaraciones = Array.isArray(programa.declaraciones) ? programa.declaraciones : [];
  for (const declaracion of declaraciones) {
    lineas.push(...unaSentencia(declaracion, 0, registro, 'declaraciones'));
  }
  const cuerpo = sentencias(programa.cuerpo, 0, registro, 'programa principal');
  // `sentencias` mete un `pass` si el bloque va vacío; a nivel de módulo un
  // programa sin nada no necesita relleno.
  lineas.push(...(cuerpo.length === 1 && cuerpo[0] === 'pass' ? [] : cuerpo));

  if (registro.hayProblemas()) return traduccionFallida(registro.motivos());
  return traduccionOk(unirLineas(lineas));
}
